import { CheckCircle2, XCircle, AlertTriangle, BookOpen, Receipt, Landmark, Check, X, Copy, FileDown, Share2, Lightbulb, MessageSquareWarning } from "lucide-react";
import { toast } from "sonner";
import { downloadAnalysisPdf, entryToText } from "@/lib/pdf";
import { api } from "@/lib/api";

const VERDICT = {
  correct: { label: "Entry appears correct", icon: CheckCircle2, bg: "#dcfce7", text: "#064e3b", border: "#86efac" },
  incorrect: { label: "Entry appears incorrect", icon: XCircle, bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  partially_correct: { label: "Partially correct", icon: AlertTriangle, bg: "#fef3c7", text: "#78350f", border: "#fcd34d" },
  suggested: { label: "Suggested entry", icon: Lightbulb, bg: "#0f172a", text: "#ffffff", border: "#0f172a" },
};

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const NoteList = ({ title, items, Icon }) => {
  if (!items?.length) return null;
  return (
    <div className="border-t border-stone-200 p-6">
      <p className="overline mb-3 flex items-center gap-2 text-slate-500"><Icon className="h-3.5 w-3.5" />{title}</p>
      <ul className="space-y-2">
        {items.map((n, i) => (
          <li key={i} className="flex gap-2 text-sm leading-relaxed text-slate-700">
            <span className="mt-1.5 h-1 w-1 shrink-0 bg-[#0f172a]" />{n}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const VerdictPanel = ({ result, compact = false, shareable = true }) => {
  const v = VERDICT[result.verdict] || VERDICT.partially_correct;
  const Icon = v.icon;

  const copyCorrected = async () => {
    const lines = result.corrected_entry?.length ? result.corrected_entry : result.lines;
    const text = `${entryToText(lines)}${result.narration ? `\n(${result.narration})` : ""}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Corrected entry copied");
    } catch {
      toast.error("Could not copy — select and copy manually");
    }
  };

  const share = async () => {
    try {
      const { data } = await api.post(`/analyses/${result.analysis_id}/share`);
      const url = `${window.location.origin}/s/${data.share_id}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Read-only link copied to clipboard");
      } catch {
        toast.success(url);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create a share link");
    }
  };

  return (
    <div className="animate-slide-left border border-stone-200 bg-white" data-testid="verdict-card">
      <div className="flex items-start gap-3 p-6" style={{ background: v.bg, color: v.text, borderBottom: `1px solid ${v.border}` }}>
        <Icon className="mt-0.5 h-6 w-6 shrink-0" />
        <div>
          <p className="font-display text-xl font-bold tracking-tight" data-testid="verdict-label">{v.label}</p>
          <p className="mt-1 font-mono-data text-xs uppercase tracking-wider opacity-80">
            {result.framework === "indas" ? "Ind AS" : "Indian GAAP (AS)"} · confidence {result.confidence}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-stone-200 p-4">
        <button onClick={copyCorrected} data-testid="copy-entry-btn"
          className="inline-flex items-center gap-1.5 border border-stone-300 px-3 py-2 text-xs text-slate-600 transition-colors duration-150 hover:border-[#0f172a] hover:text-[#0f172a]">
          <Copy className="h-3.5 w-3.5" /> Copy corrected entry
        </button>
        <button onClick={() => { downloadAnalysisPdf(result); toast.success("PDF downloaded"); }} data-testid="download-pdf-btn"
          className="inline-flex items-center gap-1.5 border border-stone-300 px-3 py-2 text-xs text-slate-600 transition-colors duration-150 hover:border-[#0f172a] hover:text-[#0f172a]">
          <FileDown className="h-3.5 w-3.5" /> Export PDF
        </button>
        {shareable && result.user_id && (
          <button onClick={share} data-testid="share-btn"
            className="inline-flex items-center gap-1.5 border border-stone-300 px-3 py-2 text-xs text-slate-600 transition-colors duration-150 hover:border-[#0f172a] hover:text-[#0f172a]">
            <Share2 className="h-3.5 w-3.5" /> Share link
          </button>
        )}
      </div>

      {result.corrected_entry?.length > 0 && (
        <div className="border-b border-stone-200 p-6" data-testid="corrected-entry">
          <p className="overline mb-3 text-slate-500">{result.verdict === "suggested" ? "The entry" : "Suggested entry"}</p>
          <table className="w-full border-collapse font-mono-data text-sm">
            <tbody>
              {result.corrected_entry.map((l, i) => (
                <tr key={i} className="border-b border-stone-100">
                  <td className="w-10 py-2 font-semibold">{l.type === "debit" ? "Dr" : "Cr"}</td>
                  <td className={`py-2 ${l.type === "credit" ? "pl-6" : ""}`}>{l.account}</td>
                  <td className="py-2 text-right">{money(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-2 border-b border-stone-200">
        <div className="border-r border-stone-200 p-6">
          <p className="overline text-slate-500">Total Debit</p>
          <p className="mt-1 font-mono-data text-lg font-semibold" data-testid="total-debit">{money(result.total_debit)}</p>
        </div>
        <div className="p-6">
          <p className="overline text-slate-500">Total Credit</p>
          <p className="mt-1 font-mono-data text-lg font-semibold" data-testid="total-credit">{money(result.total_credit)}</p>
        </div>
      </div>

      <div className="p-6" data-testid="verdict-explanation">
        <p className="overline mb-3 text-slate-500">{result.verdict === "suggested" ? "How this entry works" : "Reviewer's note"}</p>
        <p className="text-sm leading-relaxed text-slate-700">{result.explanation}</p>
      </div>

      <NoteList title="Assumptions made" items={result.assumptions} Icon={MessageSquareWarning} />

      {!compact && result.rule_checks?.length > 0 && (
        <div className="border-t border-stone-200 p-6" data-testid="rule-checks">
          <p className="overline mb-3 text-slate-500">Rule engine checks</p>
          <ul className="divide-y divide-stone-100">
            {result.rule_checks.map((c, i) => (
              <li key={i} className="flex items-start gap-3 py-2.5">
                <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center ${c.passed ? "bg-emerald-700" : "bg-red-700"} text-white`}>
                  {c.passed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                </span>
                <div>
                  <p className="text-sm font-medium">{c.label}</p>
                  <p className="font-mono-data text-xs text-slate-500">{c.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <NoteList title="Standard references" items={result.standard_refs} Icon={BookOpen} />
      <NoteList title="GST notes" items={result.gst_notes} Icon={Receipt} />
      <NoteList title="Companies Act 2013" items={result.companies_act_notes} Icon={Landmark} />
    </div>
  );
};
